import { 
  Firestore, 
  collection, 
  query, 
  where, 
  getDocs, 
  doc,
  getDoc,
  writeBatch,
  orderBy,
  limit as firestoreLimit,
  serverTimestamp
} from "firebase/firestore";
import { CampLocation } from "@/types/campLocation";
import { getCampLocationsFromOrg } from "./getCampLocationsFromOrg";
import { getLatestLocationVersion } from "./locationVersion/versionStatusService";
import { isLocationEditLiveSource } from "@/config/locationEditFlag";

// Import the sanitizeForFirestore function from campLocationUpdateService
// We need to define our own version here to avoid circular dependencies
const sanitizeForFirestore = (data: Record<string, any>): Record<string, any> => {
  const result: Record<string, any> = {};
  
  for (const [key, value] of Object.entries(data)) {
    if (value === undefined) {
      result[key] = null;
    } else if (value === null) {
      result[key] = null;
    } else if (Array.isArray(value)) {
      result[key] = value.map(item => 
        typeof item === 'object' ? sanitizeForFirestore(item) : item === undefined ? null : item
      );
    } else if (typeof value === 'object' && value !== null) {
      result[key] = sanitizeForFirestore(value);
    } else {
      result[key] = value;
    }
  }
  
  return result;
};

/**
 * Fields that should be copied in bulk operations
 * These correspond to the UI sections below the bulk copy button
 */
export const COPYABLE_FIELDS = [
  // Additional Information section
  'doesWaitlist',
  'doesScholarships', 
  'scholarshipNotes',
  
  // Camp Experience sections
  'campExperiences',
  'allergyKeys',
  'allergyNotes',
  'religiousAffiliationKeys',
  'religiousAffiliationNotes',
  'supportForKeys',
  'supportForNotes'
] as const;

export type CopyableField = typeof COPYABLE_FIELDS[number];

export interface BulkCopyResult {
  success: boolean;
  copiedCount: number;
  skippedCount: number;
  errors: string[];
  operationId: string;
}

export interface LocationSummary {
  id: string;
  name: string;
}

/**
 * Validates that the user has admin privileges
 */
const validateAdminAccess = (userRole?: string): void => {
  if (userRole !== 'admin_user') {
    throw new Error('Bulk copy operations are only available to admin users');
  }
};

/**
 * Extracts only the copyable fields from a location object
 */
export const extractCopyableFields = (location: CampLocation): Partial<CampLocation> => {
  const copyableData: Partial<CampLocation> = {};
  
  COPYABLE_FIELDS.forEach(field => {
    if (location[field] !== undefined) {
      (copyableData as any)[field] = location[field];
    }
  });
  
  return copyableData;
};

/**
 * Gets all locations for a given organization (excluding the source location)
 */
export const getTargetLocationsForOrg = async (
  db: Firestore, 
  orgId: string, 
  excludeLocationId: string
): Promise<LocationSummary[]> => {
  try {
    console.log(`[bulkLocationCopyService] Getting target locations for org ${orgId}, excluding ${excludeLocationId}`);
    
    const allLocations = await getCampLocationsFromOrg(db, orgId);
    
    // Filter out the source location
    const targetLocations = allLocations
      .filter(location => location.id !== excludeLocationId)
      .map(location => ({
        id: location.id,
        name: location.name || 'Unnamed Location'
      }));
    
    console.log(`[bulkLocationCopyService] Found ${targetLocations.length} target locations`);
    return targetLocations;
  } catch (error) {
    console.error('[bulkLocationCopyService] Error getting target locations:', error);
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    throw new Error(`Failed to fetch locations for organization: ${errorMessage}`);
  }
};

/**
 * Copies specific fields from source location to all other locations in the same organization
 */
export const bulkCopyLocationData = async (
  db: Firestore,
  sourceLocationId: string,
  userId: string,
  userRole?: string
): Promise<BulkCopyResult> => {
  const operationId = `bulk_copy_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  
  try {
    console.log(`[bulkLocationCopyService] Starting bulk copy operation ${operationId}`);
    console.log(`[bulkLocationCopyService] Source: ${sourceLocationId}, User: ${userId}, Role: ${userRole}`);
    
    // Validate admin access
    validateAdminAccess(userRole);

    // KB-3308: live-only path reads the source's live doc and writes each target's live doc.
    if (isLocationEditLiveSource()) {
      return bulkCopyLocationDataLiveOnly(db, sourceLocationId, userId, operationId);
    }

    // Get the source location's latest draft version
    const sourceVersion = await getLatestLocationVersion(db, sourceLocationId);
    if (!sourceVersion) {
      throw new Error('Source location has no draft version to copy from');
    }
    
    // Extract copyable fields from source
    const sourceData = extractCopyableFields(sourceVersion.data);
    const sanitizedData = sanitizeForFirestore(sourceData);
    
    console.log(`[bulkLocationCopyService] Extracted ${Object.keys(sanitizedData).length} copyable fields`);
    
    // Get the organization ID from source location
    const sourceOrgId = sourceVersion.data.providedByOrg?.id || sourceVersion.data.providedByOrg;
    if (!sourceOrgId) {
      throw new Error('Source location has no organization ID');
    }
    
    // Get all target locations in the same organization
    const targetLocations = await getTargetLocationsForOrg(db, sourceOrgId, sourceLocationId);
    
    if (targetLocations.length === 0) {
      return {
        success: true,
        copiedCount: 0,
        skippedCount: 0,
        errors: [],
        operationId
      };
    }
    
    // Prepare batch operations
    const batch = writeBatch(db);
    let copiedCount = 0;
    const errors: string[] = [];
    
    // Process each target location
    for (const targetLocation of targetLocations) {
      try {
        // Get the latest draft version for this target location
        const targetVersion = await getLatestLocationVersion(db, targetLocation.id);
        
        if (!targetVersion) {
          console.warn(`[bulkLocationCopyService] No draft version found for ${targetLocation.name} (${targetLocation.id}), skipping`);
          errors.push(`${targetLocation.name}: No draft version found`);
          continue;
        }
        
        // Create the update data by merging existing data with copyable fields
        const updateData = {
          data: {
            ...targetVersion.data,
            ...sanitizedData,
            // Preserve critical fields
            id: targetLocation.id,
          },
          // Version metadata fields
          changeDescription: "Bulk edit by admin",
          action: "update",
          timestamp: serverTimestamp(),
          user: doc(db, "users", userId)
        };
        
        // Add to batch
        const versionRef = doc(db, "campLocationVersions", targetVersion.versionId);
        batch.update(versionRef, updateData);
        
        copiedCount++;
        console.log(`[bulkLocationCopyService] Queued update for ${targetLocation.name} (${targetLocation.id})`);
        
      } catch (error) {
        console.error(`[bulkLocationCopyService] Error processing ${targetLocation.name}:`, error);
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        errors.push(`${targetLocation.name}: ${errorMessage}`);
      }
    }
    
    // Execute the batch operation
    if (copiedCount > 0) {
      await batch.commit();
      console.log(`[bulkLocationCopyService] Batch operation completed: ${copiedCount} locations updated`);
    }
    
    // Log the operation for audit trail
    console.log(`[bulkLocationCopyService] Operation ${operationId} completed:`, {
      sourceLocationId,
      userId,
      targetCount: targetLocations.length,
      copiedCount,
      errorCount: errors.length,
      copiedFields: Object.keys(sanitizedData),
      timestamp: new Date().toISOString()
    });
    
    return {
      success: errors.length < targetLocations.length, // Success if at least some locations were updated
      copiedCount,
      skippedCount: targetLocations.length - copiedCount,
      errors,
      operationId
    };
    
  } catch (error) {
    console.error(`[bulkLocationCopyService] Bulk copy operation ${operationId} failed:`, error);
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    throw new Error(`Bulk copy operation failed: ${errorMessage}`);
  }
};

/**
 * KB-3308: live-only bulk copy. Reads the source's live campLocations doc and writes the copyable
 * fields directly onto each target's live doc (with the editor stamp in the same write), never
 * touching campLocationVersions. No draft version is required on source or targets.
 */
const bulkCopyLocationDataLiveOnly = async (
  db: Firestore,
  sourceLocationId: string,
  userId: string,
  operationId: string,
): Promise<BulkCopyResult> => {
  const sourceRef = doc(db, "campLocations", sourceLocationId);
  const sourceSnap = await getDoc(sourceRef);
  if (!sourceSnap.exists()) {
    throw new Error('Source location not found');
  }
  const sourceLive = sourceSnap.data() as CampLocation;

  const sanitizedData = sanitizeForFirestore(extractCopyableFields(sourceLive));
  console.log(`[bulkLocationCopyService] (live-only) extracted ${Object.keys(sanitizedData).length} copyable fields`);

  const providedByOrg = (sourceLive as { providedByOrg?: { id?: string } | string }).providedByOrg;
  const sourceOrgId = typeof providedByOrg === 'string' ? providedByOrg : providedByOrg?.id;
  if (!sourceOrgId) {
    throw new Error('Source location has no organization ID');
  }

  const targetLocations = await getTargetLocationsForOrg(db, sourceOrgId, sourceLocationId);
  if (targetLocations.length === 0) {
    return { success: true, copiedCount: 0, skippedCount: 0, errors: [], operationId };
  }

  const batch = writeBatch(db);
  const userRef = doc(db, "users", userId);
  let copiedCount = 0;
  const errors: string[] = [];

  for (const targetLocation of targetLocations) {
    try {
      const targetRef = doc(db, "campLocations", targetLocation.id);
      // Merge only the copyable fields onto the live doc; the editor stamp rides the same write.
      batch.update(targetRef, {
        ...sanitizedData,
        lastEditedBy: userRef,
        lastEditedAt: serverTimestamp(),
      });
      copiedCount++;
      console.log(`[bulkLocationCopyService] (live-only) queued update for ${targetLocation.name} (${targetLocation.id})`);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      errors.push(`${targetLocation.name}: ${errorMessage}`);
    }
  }

  if (copiedCount > 0) {
    await batch.commit();
    console.log(`[bulkLocationCopyService] (live-only) batch completed: ${copiedCount} locations updated`);
  }

  return {
    success: errors.length < targetLocations.length,
    copiedCount,
    skippedCount: targetLocations.length - copiedCount,
    errors,
    operationId,
  };
};